import { skillMessage, type SkillCapability, type SkillCatalog, type SkillFunction, type SkillJob, type SkillLabel, type SkillLastRun, type SkillProposal, type SkillResult, type SkillRunFailure } from "../hooks/useSkills";
import { canCreateCapability } from "./SkillsCapabilityDetail";
import { catalogPages, excludedReason, pageIdentity } from "./skillsCatalog";

/** Six user states, each with one action (UX v2). */
export type SkillState = "ready" | "create" | "explore" | "consent" | "later" | "repair";
export const STATE_LABEL: Record<SkillState, string> = {
  ready: "Prêt", create: "Prête à créer", explore: "À explorer", consent: "Demande ton accord", later: "Pas encore possible", repair: "À réparer",
};
export const INTERFACE_THEME = "Interface et navigation";
export const UNSORTED_THEME = "À ranger";

export type SkillEntry = {
  key: string; title: string; originalTitle: string; theme: string; interface: boolean; state: "ready" | "repair";
  fn: SkillFunction; source?: string; page: string; lastRun?: SkillLastRun; proposal?: SkillProposal;
};
export type IdeaEntry = {
  key: string; title: string; originalTitle: string; theme: string; interface: boolean; state: SkillState; reason: string;
  capability?: SkillCapability; proposal?: SkillProposal; members: string[]; pages: string[]; source?: string;
};
export type IdeaIndex = { ideas: IdeaEntry[]; raw: number; intoSkills: number; ignored: IdeaEntry[]; interfaceIdeas: IdeaEntry[] };

// Only failures showing that the page no longer matches the tested program ask for a repair.
const REPAIR_CODES = /^skills_(selector_|output_|navigation_postcondition|interface_changed|ai_path_not_observed)/;

/** Accent-, case- and parenthesis-insensitive title identity for unlabelled duplicates. */
export const normalizeTitle = (title: string) => title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  .replace(/\([^)]*\)/g, " ").replace(/[^a-z0-9]+/g, " ").trim();

const label = (catalog: SkillCatalog | null | undefined, key: string): SkillLabel | undefined => catalog?.labels?.[key];

export function skillEntries(catalog: SkillCatalog | null | undefined): SkillEntry[] {
  const entries: SkillEntry[] = [];
  for (const page of catalogPages(catalog)) for (const skill of page.skills) {
    if (!skill.fn || excludedReason(skill)) continue;
    const l = label(catalog, skill.key), lastRun = catalog?.last_runs?.[skill.key];
    entries.push({ key: skill.key, title: l?.title ?? skill.title, originalTitle: skill.title, theme: l?.theme ?? UNSORTED_THEME, interface: !!l?.interface,
      state: lastRun?.status === "error" && REPAIR_CODES.test(lastRun.error_code ?? "") ? "repair" : "ready",
      fn: skill.fn, source: skill.source, page: page.id, lastRun, proposal: skill.proposal });
  }
  return entries.sort((a, b) => a.title.localeCompare(b.title, "fr"));
}

/** Generated gaps may cite internal selector references; keep them in the details only. */
export const readableGap = (text: string) => {
  const t = text.replace(/\s*\([^()]*@s\d+[^()]*\)/g, "").replace(/@s\d+/g, "").replace(/\s{2,}/g, " ").trim();
  return t.length > 110 ? t.slice(0, 107) + "…" : t;
};

function capabilityState(c: SkillCapability): { state: SkillState; reason: string } | null {
  const kind = c.kind ?? "native";
  if (c.status === "already_known") return null;
  if (kind === "transformation") return { state: "later", reason: "calcul sur les données (L4)" };
  if (kind === "composition") return { state: "later", reason: "combinaison de skills (L4)" };
  if (c.status === "tool_required") return { state: "later", reason: "outil à compléter" };
  if (["compile_error", "test_error"].includes(c.status)) return { state: "repair", reason: "création en échec" };
  if (c.effect === "write" || c.status === "authorization_required") return { state: "consent", reason: "elle modifie le site" };
  if (canCreateCapability(c)) return { state: "create", reason: "" };
  if (c.effect === "unknown") return { state: "explore", reason: "effet à vérifier" };
  return { state: "explore", reason: c.missing.length ? "manque : " + readableGap(c.missing[0]!) : "parcours à observer" };
}

const PRIORITY: SkillState[] = ["create", "repair", "explore", "consent", "later"];

/** One line per business outcome: organiser duplicates first, then exact
 * normalized titles. An idea equivalent to an existing skill is not repeated. */
export function ideaEntries(catalog: SkillCatalog | null | undefined): IdeaIndex {
  const empty: IdeaIndex = { ideas: [], raw: 0, intoSkills: 0, ignored: [], interfaceIdeas: [] };
  if (!catalog) return empty;
  const skills = new Set(catalog.functions.map(fn => fn.key));
  const raw = new Map<string, IdeaEntry>();
  const ignored: IdeaEntry[] = [];
  const make = (key: string, title: string, state: SkillState, reason: string, source?: string, capability?: SkillCapability, proposal?: SkillProposal): IdeaEntry => {
    const l = label(catalog, key);
    return { key, title: l?.title ?? title, originalTitle: title, theme: l?.theme ?? UNSORTED_THEME, interface: !!l?.interface, state, reason,
      capability, proposal, members: [key], pages: source ? [pageIdentity(source)] : [], source };
  };
  for (const c of catalog.capabilities ?? []) {
    if (c.status === "exploration_step" || skills.has(c.key)) continue;
    const s = capabilityState(c);
    if (s) raw.set(c.key, make(c.key, c.title, s.state, s.reason, c.source_url, c));
  }
  for (const p of catalog.proposals) {
    const key = p.function_key ?? p.specification.key ?? p.id;
    if (skills.has(key) || p.status === "available" || p.status === "validating") continue;
    const capability = raw.get(key)?.capability;
    if (p.status === "ignored" || p.quality_reason || p.review_group === "legacy_navigation") {
      ignored.push(make(key, p.specification.title, "later", p.status === "ignored" ? "ignorée" : "sans résultat utile", p.source_url, capability, p));
      raw.delete(key);
      continue;
    }
    raw.set(key, make(key, p.specification.title, p.status === "error" ? "repair" : "create", p.status === "error" ? "test en échec" : "programme prêt à tester", p.source_url, capability, p));
  }
  const root = (key: string) => {
    let current = key;
    for (let hop = 0; hop < 6; hop++) {
      const next = label(catalog, current)?.duplicate_of;
      if (!next || next === key) break;
      current = next;
    }
    return current;
  };
  let intoSkills = 0;
  const groups = new Map<string, IdeaEntry[]>();
  for (const idea of raw.values()) {
    const target = root(idea.key);
    if (target !== idea.key && skills.has(target)) { intoSkills++; continue; }
    const group = label(catalog, idea.key) ? "k:" + target : "t:" + normalizeTitle(idea.originalTitle) + "|" + (idea.capability?.effect ?? "read");
    groups.set(group, [...(groups.get(group) ?? []), idea]);
  }
  const ideas: IdeaEntry[] = [], interfaceIdeas: IdeaEntry[] = [];
  for (const members of groups.values()) {
    const best = [...members].sort((a, b) => PRIORITY.indexOf(a.state) - PRIORITY.indexOf(b.state))[0]!;
    const merged = { ...best, members: members.map(m => m.key), pages: [...new Set(members.flatMap(m => m.pages))] };
    (merged.interface ? interfaceIdeas : ideas).push(merged);
  }
  const byTitle = (a: IdeaEntry, b: IdeaEntry) => a.title.localeCompare(b.title, "fr");
  return { ideas: ideas.sort(byTitle), raw: raw.size, intoSkills, ignored: ignored.sort(byTitle), interfaceIdeas: interfaceIdeas.sort(byTitle) };
}

/** Items still shown with their original presentation. */
export function unlabelledCount(catalog: SkillCatalog | null | undefined): number {
  if (!catalog) return 0;
  const keys = new Set([...catalog.functions.map(f => f.key), ...(catalog.capabilities ?? []).filter(c => c.status !== "exploration_step").map(c => c.key)]);
  return [...keys].filter(key => !catalog.labels?.[key]).length;
}

export function groupByTheme<T extends { theme: string }>(items: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) groups.set(item.theme, [...(groups.get(item.theme) ?? []), item]);
  // Largest themes first; unsorted items last.
  return [...groups.entries()].sort(([a, x], [b, y]) => Number(a === UNSORTED_THEME) - Number(b === UNSORTED_THEME) || y.length - x.length || a.localeCompare(b, "fr"));
}

export function ago(iso?: string | null, now = Date.now()): string {
  const at = Date.parse(iso ?? "");
  if (!Number.isFinite(at)) return "";
  const s = (now - at) / 1000;
  if (s < 60) return "à l’instant";
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  if (s < 172800) return "hier";
  return "le " + new Date(at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

export function duration(from?: string | null, to?: string | null): string {
  const ms = Date.parse(to ?? "") - Date.parse(from ?? "");
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 60000) return (ms / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 }) + " s";
  const min = Math.floor(ms / 60000), sec = Math.round((ms % 60000) / 1000);
  return `${min} min ${String(sec).padStart(2, "0")}`;
}

export function resultRows(result: SkillResult): number {
  return Math.max(1, ...Object.values(result.data).map(value => Array.isArray(value) ? value.length : 1));
}

export const isActive = (job: SkillJob) => ["running", "queued"].includes(job.status);

const OPERATION: Record<string, string> = {
  navigate: "ouverture de la page", inspect_control: "ouverture d’un contrôle de la page", click_link: "clic sur un lien",
  click_tab: "clic sur un onglet", scroll: "défilement", wait_selector: "attente d’un élément", extract: "lecture", output_check: "contrôle du résultat",
};

/** Where a run stopped, in words; never a page value. */
export function describeFailure(failure?: SkillRunFailure | null): string {
  if (!failure) return "";
  if (failure.field) return `à la lecture du champ « ${failure.field} »`;
  if (failure.operation === "output_check") return "au contrôle du résultat";
  const what = OPERATION[failure.operation ?? ""] ?? "une étape";
  return failure.step ? `à l’étape ${failure.step}/${failure.steps ?? failure.step} (${what})` : `à l’${what}`;
}

/** Run-time wording: the tested program met a page it does not recognise. */
export function runMessage(code?: string | null): string {
  switch (code) {
    case "skills_selector_missing_or_ambiguous": return "un élément attendu est introuvable ou présent plusieurs fois dans la page.";
    case "skills_selector_timeout": return "un élément attendu n’est pas apparu dans la page.";
    case "skills_interaction_postcondition_failed": return "le contrôle n’a pas ouvert la vue attendue.";
    case "skills_output_invalid": return "le résultat lu ne respecte pas le contrat du skill (par exemple une valeur vide).";
    case "skills_output_rows_misaligned": return "les colonnes lues ne correspondent plus aux mêmes lignes.";
    default: return skillMessage(code ?? "");
  }
}
